import {
  ScreeningDtoTypeEnum,
  type LegacyProviderConfigDto,
  type ScreeningDto,
} from "@docket/consumer-app-client-typescript-axios";
import { queryOptions, type QueryFunctionContext } from "@tanstack/svelte-query";
import { api } from "../api/client";
import {
  getIzRecord,
  getIzScreeningRecord,
  getIzSearches,
  getSmartHealthLinkAccessLogs,
  getSmartHealthLinks,
  getVCICode,
} from "../api/endpoints";
import type {
  LegacyGetRecordDataDtoWithIntId,
  LegacyGetSearchDataDtoWithIntId,
} from "../api/types";
import type { ImmunizationRecord, ImmunizationSearch } from "../models/Interfaces";
import { providerConfig } from "../records/cards";
import { retryTransient } from "./client";
import { keys } from "./keys";
import { pollWhile } from "./polling";

/**
 * Server data kept for the whole session and saved for the next one (see persist.ts). It is
 * fetched when first used, and after that only when invalidated (by a refresh, a change or the
 * next session) or while it is polled. The React app kept it the same way, in atoms.
 */
export const sessionData = {
  staleTime: Infinity,
  gcTime: Infinity,
  retry: retryTransient,
} as const;

/** Whether the server is still working on a search or record: it hasn't been dequeued yet. */
export function stillQueued(item: { attributes: { dequeued_at?: string | null } }): boolean {
  return item.attributes.dequeued_at == null;
}

// ==== Immunizations

/** The uids of the searches that were still queued before and are done now. */
function finished(
  before: readonly LegacyGetSearchDataDtoWithIntId[] | undefined,
  after: readonly LegacyGetSearchDataDtoWithIntId[],
): string[] {
  const queued = new Set(before?.filter(stillQueued).map(({ attributes }) => attributes.uid));
  return after
    .filter((search) => queued.has(search.attributes.uid) && !stillQueued(search))
    .map(({ attributes }) => attributes.uid);
}

/**
 * The raw searches, as the React app stored them. Polls until the server has processed every
 * search, and refetches the record and screenings of each search it finishes.
 */
export const searchesQuery = queryOptions({
  queryKey: keys.searches,
  // (The context's type is spelled out so TypeScript infers the data type before `refetchInterval`.)
  queryFn: async ({ client, queryKey }: QueryFunctionContext<typeof keys.searches>) => {
    const before = client.getQueryData<LegacyGetSearchDataDtoWithIntId[]>(queryKey);
    const searches = (await getIzSearches(false)).data;
    for (const uid of finished(before, searches)) {
      void client.invalidateQueries({ queryKey: keys.search(uid) });
    }
    return searches;
  },
  ...sessionData,
  // The React app's poller also kept going while it couldn't get the searches.
  refetchInterval: pollWhile(
    ({ state }) => state.status === "error" || (state.data?.some(stillQueued) ?? false),
  ),
});

/**
 * A verified search's record. Polls while the server is refreshing it, then refetches the
 * search's screenings, which came from the same refresh.
 */
export function recordQuery(search: ImmunizationSearch) {
  return queryOptions({
    queryKey: keys.record(search.uid),
    queryFn: async ({ client, queryKey }: QueryFunctionContext<ReturnType<typeof keys.record>>) => {
      const before = client.getQueryData<LegacyGetRecordDataDtoWithIntId>(queryKey);
      const record = await getIzRecord(search.uid, search.izProviderKey);
      if (before !== undefined && stillQueued(before) && !stillQueued(record)) {
        void client.invalidateQueries({ queryKey: keys.screenings(search.uid) });
      }
      return record;
    },
    ...sessionData,
    meta: { notify: true },
    refetchInterval: pollWhile(({ state }) => state.data !== undefined && stillQueued(state.data)),
  });
}

const SCREENING_TYPES: readonly string[] = Object.values(ScreeningDtoTypeEnum);

/** A verified search's screenings, fetched only where its registry's config says it has them. */
export function screeningsQuery(
  search: ImmunizationSearch,
  configs: readonly LegacyProviderConfigDto[] | undefined,
) {
  return queryOptions({
    queryKey: keys.screenings(search.uid),
    queryFn: async (): Promise<ScreeningDto[]> => {
      const screenings = await getIzScreeningRecord(search.uid, search.izProviderKey);
      if (screenings.some((screening) => !SCREENING_TYPES.includes(screening.type))) {
        throw new Error("Unknown screening type");
      }
      return screenings;
    },
    ...sessionData,
    meta: { notify: true },
    enabled: !!providerConfig(configs, search.izProviderKey)?.features?.screenings,
  });
}

// ==== SMART Health Cards and Links

/** The active links. */
export const shlQuery = queryOptions({
  queryKey: keys.shl,
  queryFn: getSmartHealthLinks,
  ...sessionData,
});

/** Who opened a link. Fetched again each time it's shown, as before. */
export function shlLogsQuery(linkId: string) {
  return queryOptions({
    queryKey: keys.shlLogs(linkId),
    queryFn: () => getSmartHealthLinkAccessLogs(linkId),
  });
}

/** The SMART Health Card QR payload for a record. Fetched again each time it's shown, as before. */
export function vciQuery(record: Pick<ImmunizationRecord, "uid" | "izProviderKey">) {
  return queryOptions({
    queryKey: keys.vci(record.uid),
    queryFn: () => getVCICode(record.uid, record.izProviderKey),
  });
}

// ==== Configuration and account

/** Which providers are live, and what they support. */
export const providerConfigsQuery = queryOptions({
  queryKey: keys.providerConfigs,
  queryFn: async () => (await api.railsLegacyV2.getProviderConfigs()).data.configs,
  ...sessionData,
});

/** The devices registered for push notifications. */
export const devicesQuery = queryOptions({
  queryKey: keys.devices,
  queryFn: async () => (await api.railsLegacyV2.getDevices()).data,
  ...sessionData,
});
